import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  TextField, MenuItem, Chip, InputAdornment, Tooltip, Grid,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  FilterAltOff as FilterOffIcon, Groups as GroupsIcon,
} from '@mui/icons-material';
import apiService from '../../services/apiServices';
import Toast from '../actions/Toasts';
import {
  DEPARTMENTS, STATUS_OPTIONS, PRIORITY_OPTIONS,
  departmentLabel, statusOption, priorityOption, fmtDate, workerName,
} from './projectConstants';
import useConfirm from '../../hooks/useConfirm';
import { activeWorkers } from '../../utils/workerStatus';
import ProjectFormDialog from './ProjectFormDialog';


const alert = { open: false, message: '', variant: 'info' };

function PriorityDot({ value }) {
  const p = priorityOption(value);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>{p.label}</Typography>
    </Box>
  );
}

export default function ProjectsList() {
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [clients, setClients]   = useState([]);
  const [workers, setWorkers]   = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [toast, setToast]       = useState(alert);

  // filters
  const [search, setSearch]         = useState('');
  const [fDept, setFDept]           = useState('');
  const [fStatus, setFStatus]       = useState('');
  const [fPriority, setFPriority]   = useState('');
  const [fManager, setFManager]     = useState('');
  const [fClient, setFClient]       = useState('');

  useEffect(() => { loadProjects(); loadClients(); loadWorkers(); }, []);

  const clientMap = useMemo(() => {
    const m = new Map();
    clients.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [clients]);

  const workerMap = useMemo(() => {
    const m = new Map();
    workers.forEach((w) => m.set(String(w.Id || w.id), w));
    return m;
  }, [workers]);

  const loadProjects = async () => {
    try {
      const r = await apiService().get('projects');
      setProjects(r.data || []);
    } catch {
      setToast({ open: true, message: 'Gabim në ngarkimin e projekteve', variant: 'error' });
    }
  };
  const loadClients = async () => {
    try { const r = await apiService().get('clients'); setClients(r.data || []); } catch {}
  };
  const loadWorkers = async () => {
    try { const r = await apiService().get('workers'); setWorkers(activeWorkers(r.data)); } catch {}
  };

  const openCreate = () => { setEditing(null); setOpenDialog(true); };
  const openEdit = (row) => { setEditing(row); setOpenDialog(true); };
  const closeDialog = () => { setOpenDialog(false); setEditing(null); };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!(await confirm({ title: 'Fshi projektin', message: 'A jeni të sigurt që doni të fshini këtë projekt?', confirmLabel: 'Fshi' }))) return;
    try {
      await apiService().delete('projects', id);
      setToast({ open: true, message: 'Projekti u fshi', variant: 'success' });
      loadProjects();
    } catch {
      setToast({ open: true, message: 'Gabim në fshirjen e projektit', variant: 'error' });
    }
  };

  // managers = workers referenced as a manager, but for the filter we offer all workers
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter((p) => {
      const dep = String(p.department || p.Department || '').toLowerCase();
      const st  = String(p.status || p.Status || '').toLowerCase();
      const pr  = String(p.priority || p.Priority || '').toLowerCase();
      const mgr = String(p.manager_id || p.ManagerId || '');
      const cl  = String(p.client_id || p.ClientId || '');
      if (fDept && dep !== fDept) return false;
      if (fStatus && st !== fStatus) return false;
      if (fPriority && pr !== fPriority) return false;
      if (fManager && mgr !== fManager) return false;
      if (fClient && cl !== fClient) return false;
      if (q) {
        const mgrName = workerName(workerMap.get(mgr)).toLowerCase();
        const clName  = (clientMap.get(cl)?.name || '').toLowerCase();
        const hay = `${p.code || p.Code || ''} ${p.name || p.Name || ''} ${departmentLabel(dep)} ${mgrName} ${clName} ${p.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [projects, search, fDept, fStatus, fPriority, fManager, fClient, workerMap, clientMap]);

  const anyFilter = search || fDept || fStatus || fPriority || fManager || fClient;
  const clearFilters = () => { setSearch(''); setFDept(''); setFStatus(''); setFPriority(''); setFManager(''); setFClient(''); };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {confirmUI}
      <Toast {...toast} />
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Projektet</Typography>
              <Typography variant="caption" color="text.secondary">
                {filtered.length} {filtered.length === 1 ? 'projekt' : 'projekte'}
                {anyFilter ? ` (nga ${projects.length})` : ''}
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
              sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>
              Shto Projekt
            </Button>
          </Box>

          {/* ── Filters ── */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" placeholder="Kërko (kod, emër, menaxher, klient...)"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Departamenti" value={fDept} onChange={(e) => setFDept(e.target.value)}>
                <MenuItem value=""><em>Të gjitha</em></MenuItem>
                {DEPARTMENTS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Statusi" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                <MenuItem value=""><em>Të gjitha</em></MenuItem>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Prioriteti" value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
                <MenuItem value=""><em>Të gjitha</em></MenuItem>
                {PRIORITY_OPTIONS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField select fullWidth size="small" label="Klienti" value={fClient} onChange={(e) => setFClient(e.target.value)}>
                <MenuItem value=""><em>Të gjithë</em></MenuItem>
                {clients.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            {anyFilter && (
              <Grid item xs={12} sm="auto">
                <Button size="small" onClick={clearFilters} startIcon={<FilterOffIcon />}
                  sx={{ textTransform: 'none' }}>Pastro filtrat</Button>
              </Grid>
            )}
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Kodi</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Projekti</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', md: 'table-cell' } }}>Departamenti</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>Menaxheri</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }} align="center">Anëtarë</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Statusi</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', sm: 'table-cell' } }}>Prioriteti</TableCell>
                  <TableCell sx={{ fontWeight: 700, display: { xs: 'none', lg: 'table-cell' } }}>Afati</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Veprime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => {
                  const st  = statusOption(row.status || row.Status);
                  const dep = String(row.department || row.Department || '').toLowerCase();
                  const mgr = workerMap.get(String(row.manager_id || row.ManagerId || ''));
                  const members = Array.isArray(row.members) ? row.members : (Array.isArray(row.Members) ? row.Members : []);
                  return (
                    <TableRow key={row.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => history.push(`/projects/${row.id}`)}>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                          {row.code || row.Code || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name || row.Name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { md: 'none' } }}>
                          {departmentLabel(dep)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip label={departmentLabel(dep)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="caption">{mgr ? workerName(mgr) : '—'}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <GroupsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{members.length}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={st.label} color={st.color} size="small" />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <PriorityDot value={row.priority || row.Priority} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="caption">
                          {fmtDate(row.start_date || row.StartDate)} → {fmtDate(row.end_date || row.EndDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edito">
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Tooltip>
                        <Tooltip title="Fshi">
                          <IconButton size="small" color="error" onClick={(e) => handleDelete(e, row.id)}><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {anyFilter ? 'Asnjë projekt nuk përputhet me filtrat' : 'Nuk ka projekte për të shfaqur'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ProjectFormDialog
        open={openDialog}
        project={editing}
        workers={workers}
        clients={clients}
        onClose={closeDialog}
        onSaved={(_, wasEditing) => {
          setOpenDialog(false);
          setEditing(null);
          setToast({ open: true, message: wasEditing ? 'Projekti u përditësua' : 'Projekti u krijua', variant: 'success' });
          loadProjects();
        }}
      />
    </Box>
  );
}
